import type {
  BackupSession,
  BackupSessionIn,
  BackupState,
  BillingInfo,
  BillingInterval,
  CallInfo,
  CallJoin,
  CallMedia,
  ChannelInfo,
  Chat,
  ChatPreferences,
  ClaimedKey,
  Device,
  Envelope,
  EscrowEvaluation,
  EscrowStatus,
  EscrowWrapIn,
  Features,
  ForwardedFile,
  GuestInvite,
  LastSmsInvite,
  MessageContext,
  MessageInfo,
  OperatorPublicKey,
  OperatorStatus,
  PhoneChangeResult,
  PhoneSettings,
  PhoneStart,
  Privacy,
  PublicUser,
  QuietHours,
  RecoveryCodeResult,
  RecoveryState,
  ReportBody,
  ServerMessage,
  ServerPin,
  ServerReminder,
  ServerScheduled,
  ServerWorkflow,
  SessionInfo,
  SharePreview,
  SignInResult,
  SmsInvite,
  Space,
  SpaceInvitePreview,
  SpaceMember,
  SpaceNotifications,
  StarPage,
  ToDeviceMessage,
  User,
  WorkflowSchedule,
  WorkflowWork,
} from "./types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    /** A machine-readable reason, such as "storage_full" when a plan limit is reached. */
    readonly code: string | null = null,
    /** Whether upgrading the person's own plan would lift the limit. */
    readonly upgrade = false,
    /** When the server will take the request again, as an ISO time, after a 429 that says so. */
    readonly retryAt: string | null = null,
  ) {
    super(detail);
  }
}

/** The plan limits the server can report, with status 402. */
export type PlanLimit = "storage_full" | "group_limit" | "call_limit";

export function isPlanLimit(err: unknown): err is ApiError & { code: PlanLimit } {
  return err instanceof ApiError && err.status === 402 && err.code !== null;
}

/** The most numbers one contact match request may carry. */
export const MATCH_BATCH = 1000;

export type ApiOptions = {
  /** Origin of the Rhema server. Empty for the web app, which shares its origin. */
  baseUrl?: string;
  /** Bearer token for desktop and mobile. The web app uses its session cookie instead. */
  token?: () => string | null;
  fetch?: typeof fetch;
};

export type Query = Record<string, string | number | undefined>;

export type RawInit = {
  body?: BodyInit;
  headers?: Record<string, string>;
  query?: Query;
  signal?: AbortSignal;
  /** Lets the request finish after the page has closed. */
  keepalive?: boolean;
};

export class Api {
  readonly baseUrl: string;
  private readonly token: () => string | null;
  private readonly doFetch: typeof fetch;

  constructor(options: ApiOptions = {}) {
    this.baseUrl = options.baseUrl ?? "";
    this.token = options.token ?? (() => null);
    this.doFetch = options.fetch ?? ((...args) => fetch(...args));
  }

  /**
   * Sends a request with this client's credentials and returns the raw response. The integrations
   * use it for streams, binary bodies and their own error shapes.
   */
  raw(method: string, path: string, init: RawInit = {}): Promise<Response> {
    const url = new URL(
      this.baseUrl + path,
      this.baseUrl || globalThis.location?.origin || "http://localhost",
    );
    for (const [k, v] of Object.entries(init.query ?? {}))
      if (v !== undefined) url.searchParams.set(k, String(v));
    const headers: Record<string, string> = { Accept: "application/json", ...init.headers };
    const token = this.token();
    if (token) headers.Authorization = `Bearer ${token}`;
    return this.doFetch(this.baseUrl ? url.toString() : url.pathname + url.search, {
      method,
      headers,
      body: init.body,
      credentials: this.baseUrl ? "omit" : "same-origin",
      ...(init.signal ? { signal: init.signal } : {}),
      ...(init.keepalive ? { keepalive: true } : {}),
    });
  }

  async request<T>(
    method: string,
    path: string,
    body?: unknown,
    query?: Query,
    init: Pick<RawInit, "keepalive"> = {},
  ): Promise<T> {
    const res = await this.raw(method, path, {
      ...init,
      query,
      ...(body === undefined
        ? {}
        : { body: JSON.stringify(body), headers: { "Content-Type": "application/json" } }),
    });
    if (res.status === 204) return undefined as T;
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = typeof data.detail === "string" ? data.detail : "Something went wrong. Try again.";
      const code = typeof data.error === "string" ? data.error : null;
      const retryAt = typeof data.retry_at === "string" ? data.retry_at : null;
      throw new ApiError(res.status, detail, code, data.upgrade === true, retryAt);
    }
    return data as T;
  }

  /**
   * Switches the server sets for every app, read before sign-in. A server that predates them, or
   * cannot be reached, answers with everything off.
   */
  async features(): Promise<Features> {
    try {
      const res = await this.request<Partial<Features>>("GET", "/api/features");
      return { phone: res.phone === true, sms_invites: res.sms_invites === true };
    } catch {
      return { phone: false, sms_invites: false };
    }
  }

  /** Has the server text an invite to Rhema, for desktops, which cannot text. */
  sendSmsInvite(phone: string) {
    return this.request<void>("POST", "/api/invites/sms", { phone });
  }
  /**
   * The last invite the server texted for you to this number, and when the next one can go. The
   * number goes in the body, so it stays out of addresses and logs.
   */
  lastSmsInvite(phone: string) {
    return this.request<LastSmsInvite>("POST", "/api/invites/sms/last", { phone });
  }
  /** The invites the server texted for you in the last month, newest first, with their delivery. */
  sentSmsInvites() {
    return this.request<SmsInvite[]>("GET", "/api/invites/sms/sent");
  }

  // Sign-in

  /** Asks SiliconBlast to text a 6-digit code to an E.164 number. */
  phoneStart(phone: string, client: "web" | "native") {
    return this.request<PhoneStart>("POST", "/auth/phone/start", { phone, client });
  }
  phoneVerify(startToken: string, code: string, client: "web" | "native") {
    return this.request<SignInResult>("POST", "/auth/phone/verify", {
      start_token: startToken,
      code,
      client,
    });
  }
  /** The two-step PIN, when phoneVerify answered pin_required. */
  phonePin(pinToken: string, pin: string, client: "web" | "native") {
    return this.request<SignInResult>("POST", "/auth/phone/pin", { pin_token: pinToken, pin, client });
  }
  /** Asks SBI to email a PIN reset to the account's recovery address, if it has one. */
  phonePinRecover(pinToken: string) {
    return this.request<{ status: "ok" }>("POST", "/auth/phone/pin/recover", { pin_token: pinToken });
  }
  passwordLogin(email: string, password: string, client: "web" | "native") {
    return this.request<SignInResult>("POST", "/auth/password/login", { email, password, client });
  }
  mfa(mfaToken: string, code: string, client: "web" | "native") {
    return this.request<{ user: User; token?: string }>("POST", "/auth/mfa", {
      mfa_token: mfaToken,
      code,
      client,
    });
  }
  register(body: { email: string; password: string; first_name?: string; last_name?: string }) {
    return this.request<{ verification_required?: boolean; user?: User }>(
      "POST",
      "/auth/password/register",
      body,
    );
  }
  verifyEmail(token: string) {
    return this.request<{ status: string }>("POST", "/auth/verify-email", { token });
  }
  forgotPassword(email: string) {
    return this.request<{ status: string }>("POST", "/auth/password/forgot", { email });
  }
  /** Sets a new two-step PIN from the link in a PIN reset email. */
  phonePinReset(token: string, pin: string) {
    return this.request<{ status: string }>("POST", "/auth/phone/pin/reset", { token, pin });
  }
  resetPassword(token: string, password: string) {
    return this.request<{ status: string }>("POST", "/auth/password/reset", { token, password });
  }
  startNative(idp?: string) {
    return this.request<{ url: string; state: string }>("GET", "/auth/start", undefined, {
      client: "native",
      format: "json",
      idp,
    });
  }
  nativeExchange(code: string, state: string) {
    return this.request<{ token: string; user: User }>("POST", "/auth/native/exchange", { code, state });
  }
  logout() {
    return this.request<{ logout_url: string }>("POST", "/auth/logout");
  }

  // People

  me() {
    return this.request<User>("GET", "/api/me");
  }
  updateMe(body: Partial<Pick<User, "display_name" | "about" | "handle">>) {
    return this.request<User>("PATCH", "/api/me", body);
  }
  /** Deletes the Rhema account for good. The SiliconBlast sign-in stays. */
  deleteAccount() {
    return this.request<void>("DELETE", "/api/me", { confirm: "DELETE" });
  }
  /**
   * Finds which of up to 1,000 E.164 numbers belong to people on Rhema, and saves them as contacts.
   * The server checks the numbers and does not keep them. See matchAllContacts for longer lists.
   */
  matchContacts(numbers: string[]) {
    return this.request<PublicUser[]>("POST", "/api/contacts/match", { numbers });
  }
  /** Matches any number of E.164 numbers, 1,000 per request. */
  async matchAllContacts(numbers: string[]): Promise<PublicUser[]> {
    const found = new Map<string, PublicUser>();
    for (let i = 0; i < numbers.length; i += MATCH_BATCH) {
      for (const person of await this.matchContacts(numbers.slice(i, i + MATCH_BATCH)))
        found.set(person.id, person);
    }
    return [...found.values()];
  }
  /**
   * The people with exactly this email address. Only for servers with phone sign-in switched off;
   * the others answer 404.
   */
  findByEmail(email: string) {
    return this.request<PublicUser[]>("GET", "/api/users/search", undefined, { q: email.trim() });
  }
  /** The person with this number. Rejects with status 404 when the number is not on Rhema. */
  userByPhone(e164: string) {
    return this.request<PublicUser>("GET", `/api/users/by-phone/${e164.replace(/\D/g, "")}`);
  }
  /** Your phone number and two-step verification, as SBI holds them. */
  phoneSettings() {
    return this.request<PhoneSettings>("GET", "/api/account/phone");
  }
  /** Texts a code to a number you want to add, or to change to. */
  phoneChangeStart(phone: string) {
    return this.request<PhoneStart>("POST", "/api/account/phone/start", { phone });
  }
  phoneChangeVerify(startToken: string, code: string) {
    return this.request<PhoneChangeResult>("POST", "/api/account/phone/verify", {
      start_token: startToken,
      code,
    });
  }
  /** The two-step PIN of the account that already had the number, after its code. */
  phoneChangePin(pinToken: string, pin: string) {
    return this.request<PhoneChangeResult>("POST", "/api/account/phone/verify/pin", {
      pin_token: pinToken,
      pin,
    });
  }
  /** Sets your profile photo from a JPEG, PNG or WebP, sent as base64. */
  setAvatar(photo: string) {
    return this.request<User>("PUT", "/api/me/avatar", { photo });
  }
  removeAvatar() {
    return this.request<User>("DELETE", "/api/me/avatar");
  }
  removePhone() {
    return this.request<User>("DELETE", "/api/account/phone");
  }
  /** Turns on two-step verification, or changes the PIN. Leaving out the email removes it. */
  setPhonePin(pin: string, recoveryEmail?: string) {
    return this.request<PhoneSettings>("PUT", "/api/account/phone/pin", {
      pin,
      recovery_email: recoveryEmail || null,
    });
  }
  removePhonePin() {
    return this.request<PhoneSettings>("DELETE", "/api/account/phone/pin");
  }
  privacy() {
    return this.request<Privacy>("GET", "/api/me/privacy");
  }
  updatePrivacy(body: Partial<Privacy>) {
    return this.request<Privacy>("PATCH", "/api/me/privacy", body);
  }
  user(id: string) {
    return this.request<PublicUser>("GET", `/api/users/${id}`);
  }
  contacts() {
    return this.request<PublicUser[]>("GET", "/api/contacts");
  }
  addContact(id: string) {
    return this.request<void>("PUT", `/api/contacts/${id}`);
  }
  removeContact(id: string) {
    return this.request<void>("DELETE", `/api/contacts/${id}`);
  }
  block(id: string) {
    return this.request<void>("PUT", `/api/blocks/${id}`);
  }
  unblock(id: string) {
    return this.request<void>("DELETE", `/api/blocks/${id}`);
  }
  /**
   * Reports a person or a chat to SiliconBlast. The server cannot read messages, so only the
   * decrypted messages the reporter chooses to include are sent.
   */
  report(body: ReportBody) {
    return this.request<void>("POST", "/api/reports", body);
  }
  sessions() {
    return this.request<SessionInfo[]>("GET", "/api/me/sessions");
  }
  revokeSession(id: string) {
    return this.request<void>("DELETE", `/api/me/sessions/${id}`);
  }
  presence(ids: string[]) {
    return this.request<Record<string, { online: boolean; last_seen: string | null }>>(
      "GET",
      "/api/presence",
      undefined,
      { ids: ids.join(",") },
    );
  }

  // Devices and keys

  registerDevice(body: {
    name: string;
    platform: Device["platform"];
    curve25519: string;
    ed25519: string;
    one_time_keys: Record<string, string>;
    fallback_key: string | null;
  }) {
    return this.request<Device>("POST", "/api/devices", body);
  }
  uploadKeys(body: { one_time_keys: Record<string, string>; fallback_key?: string | null }) {
    return this.request<{ one_time_key_count: number }>("POST", "/api/devices/keys", body);
  }
  myDevice() {
    return this.request<{ device: Device | null; one_time_key_count: number }>("GET", "/api/devices/me");
  }
  userDevices(userId: string) {
    return this.request<Device[]>("GET", `/api/users/${userId}/devices`);
  }
  claimKeys(deviceIds: string[]) {
    return this.request<Record<string, ClaimedKey>>("POST", "/api/keys/claim", { device_ids: deviceIds });
  }
  sendToDevice(messages: { device_id: string; type: string; content: Record<string, unknown> }[]) {
    return this.request<void>("POST", "/api/to-device", { messages });
  }
  toDevice(after: number) {
    return this.request<{ messages: ToDeviceMessage[]; next: number }>("GET", "/api/to-device", undefined, {
      after,
    });
  }

  // Encrypted key backup

  keyBackup() {
    return this.request<BackupState>("GET", "/api/keys/backup/version");
  }
  newKeyBackup(body: { id: string; check: string; replaces: string | null }) {
    return this.request<BackupState>("POST", "/api/keys/backup/version", body);
  }
  putKeyBackup(version: string, sessions: BackupSessionIn[]) {
    return this.request<{ stored: number; kept: number; skipped: number }>("POST", "/api/keys/backup", {
      version,
      sessions,
    });
  }
  keyBackupSessions(since: number, chat?: string) {
    return this.request<{ sessions: BackupSession[]; next: number; more: boolean }>(
      "GET",
      "/api/keys/backup",
      undefined,
      { since, chat },
    );
  }
  escrowStatus() {
    return this.request<EscrowStatus>("GET", "/api/keys/backup/escrow");
  }
  escrowEvaluate(blinded: string, fresh = false) {
    return this.request<EscrowEvaluation>("POST", "/api/keys/backup/escrow/evaluate", {
      blinded,
      new: fresh,
    });
  }
  escrowNonce() {
    return this.request<{ nonce: string }>("POST", "/api/keys/backup/escrow/nonce");
  }
  escrowProof(version: string, mac: string) {
    return this.request<EscrowStatus>("POST", "/api/keys/backup/escrow/proof", { version, mac });
  }
  putEscrow(keyId: string, wraps: EscrowWrapIn[]) {
    return this.request<EscrowStatus>("PUT", "/api/keys/backup/escrow", { key_id: keyId, wraps });
  }
  operatorPublicKey() {
    return this.request<OperatorPublicKey>("GET", "/api/keys/escrow/operator-public-key");
  }
  operatorStatus() {
    return this.request<OperatorStatus>("GET", "/api/keys/escrow/operator");
  }
  putOperatorWraps(fingerprint: string, wraps: { version: string; wrapped: string }[]) {
    return this.request<OperatorStatus>("PUT", "/api/keys/escrow/operator", { fingerprint, wraps });
  }
  setAdvancedProtection(enabled: boolean) {
    return this.request<OperatorStatus>("PUT", "/api/keys/escrow/advanced-protection", { enabled });
  }
  recoveryState() {
    return this.request<RecoveryState>("GET", "/api/keys/escrow/recovery");
  }
  useRecoveryCode(verifier: string) {
    return this.request<RecoveryCodeResult>("POST", "/api/keys/escrow/recovery", { verifier });
  }

  // Chats

  chats() {
    return this.request<Chat[]>("GET", "/api/chats");
  }
  chat(id: string) {
    return this.request<Chat>("GET", `/api/chats/${id}`);
  }
  createDirect(userId: string) {
    return this.request<Chat>("POST", "/api/chats", { kind: "direct", user_id: userId });
  }
  createGroup(title: string, memberIds: string[]) {
    return this.request<Chat>("POST", "/api/chats", { kind: "group", title, member_ids: memberIds });
  }
  /** The person's own Queravel chat, created on first use. */
  assistantChat() {
    return this.request<Chat>("POST", "/api/chats/assistant");
  }
  // Spaces

  spaces() {
    return this.request<Space[]>("GET", "/api/spaces");
  }
  space(spaceId: string) {
    return this.request<Space>("GET", `/api/spaces/${spaceId}`);
  }
  createSpace(name: string, about = "") {
    return this.request<Space>("POST", "/api/spaces", { name, about });
  }
  updateSpace(
    spaceId: string,
    body: {
      name?: string;
      about?: string;
      apps?: Partial<Record<"queravel" | "malakh" | "penielly", boolean>>;
    },
  ) {
    return this.request<Space>("PATCH", `/api/spaces/${spaceId}`, body);
  }
  deleteSpace(spaceId: string) {
    return this.request<void>("DELETE", `/api/spaces/${spaceId}`);
  }
  leaveSpace(spaceId: string) {
    return this.request<void>("POST", `/api/spaces/${spaceId}/leave`);
  }
  spaceMembers(spaceId: string) {
    return this.request<SpaceMember[]>("GET", `/api/spaces/${spaceId}/members`);
  }
  setSpaceRole(spaceId: string, userId: string, role: "admin" | "member") {
    return this.request<void>("PATCH", `/api/spaces/${spaceId}/members/${userId}`, { role });
  }
  removeFromSpace(spaceId: string, userId: string) {
    return this.request<void>("DELETE", `/api/spaces/${spaceId}/members/${userId}`);
  }
  spaceInvite(spaceId: string) {
    return this.request<{ code: string }>("POST", `/api/spaces/${spaceId}/invite`);
  }
  revokeSpaceInvite(spaceId: string) {
    return this.request<void>("DELETE", `/api/spaces/${spaceId}/invite`);
  }
  previewSpaceInvite(code: string) {
    return this.request<SpaceInvitePreview>("GET", `/api/spaces/join/${encodeURIComponent(code)}`);
  }
  joinSpace(code: string) {
    return this.request<Space>("POST", `/api/spaces/join/${encodeURIComponent(code)}`);
  }
  channels(spaceId: string) {
    return this.request<ChannelInfo[]>("GET", `/api/spaces/${spaceId}/channels`);
  }
  createChannel(
    spaceId: string,
    body: { name: string; topic?: string; private?: boolean; announcement?: boolean; everyone?: boolean },
  ) {
    return this.request<Chat>("POST", `/api/spaces/${spaceId}/channels`, body);
  }
  joinChannel(spaceId: string, chatId: string) {
    return this.request<Chat>("POST", `/api/spaces/${spaceId}/channels/${chatId}/join`);
  }
  leaveChannel(spaceId: string, chatId: string) {
    return this.request<void>("POST", `/api/spaces/${spaceId}/channels/${chatId}/leave`);
  }
  addToChannel(spaceId: string, chatId: string, userIds: string[]) {
    return this.request<Chat>("POST", `/api/spaces/${spaceId}/channels/${chatId}/members`, {
      user_ids: userIds,
    });
  }
  /**
   * Takes someone out of a channel, for its admins and the owning space's admins. Use the client's
   * removeFromChannel, which also rotates the channel's keys.
   */
  removeFromChannel(spaceId: string, chatId: string, userId: string) {
    return this.request<void>("DELETE", `/api/spaces/${spaceId}/channels/${chatId}/members/${userId}`);
  }
  /** Your notifications for a space, which its channels follow. */
  spaceNotifications(spaceId: string) {
    return this.request<SpaceNotifications>("GET", `/api/spaces/${spaceId}/notifications`);
  }
  setSpaceNotifications(spaceId: string, body: { mentions_only?: boolean; muted_until?: string | null }) {
    return this.request<SpaceNotifications>("PATCH", `/api/spaces/${spaceId}/notifications`, body);
  }
  /** Mark all as read: every channel of the space you are in. Answers the chats that changed. */
  markSpaceRead(spaceId: string) {
    return this.request<{ chat_ids: string[] }>("POST", `/api/spaces/${spaceId}/read`);
  }
  setSpacePhoto(spaceId: string, photo: string) {
    return this.request<Space>("PUT", `/api/spaces/${spaceId}/photo`, { photo });
  }
  removeSpacePhoto(spaceId: string) {
    return this.request<Space>("DELETE", `/api/spaces/${spaceId}/photo`);
  }
  /** A group's or channel's photo, base64. */
  setChatPhoto(chatId: string, photo: string) {
    return this.request<Chat>("PUT", `/api/chats/${chatId}/photo`, { photo });
  }
  removeChatPhoto(chatId: string) {
    return this.request<Chat>("DELETE", `/api/chats/${chatId}/photo`);
  }
  /** Every channel in the space, private ones included, for its admins. */
  allChannels(spaceId: string) {
    return this.request<ChannelInfo[]>("GET", `/api/spaces/${spaceId}/channels`, undefined, { all: "true" });
  }
  updateChannel(spaceId: string, chatId: string, body: { private?: boolean; auto_join?: boolean }) {
    return this.request<ChannelInfo>("PATCH", `/api/spaces/${spaceId}/channels/${chatId}`, body);
  }
  deleteChannel(spaceId: string, chatId: string) {
    return this.request<void>("DELETE", `/api/spaces/${spaceId}/channels/${chatId}`);
  }

  // Space workflows

  workflows(spaceId: string) {
    return this.request<ServerWorkflow[]>("GET", `/api/spaces/${spaceId}/workflows`);
  }
  createWorkflow(
    spaceId: string,
    body: {
      chat_id: string;
      kind: "welcome" | "recurring";
      name: string;
      definition: Envelope;
    } & Partial<WorkflowSchedule>,
  ) {
    return this.request<ServerWorkflow>("POST", `/api/spaces/${spaceId}/workflows`, body);
  }
  updateWorkflow(
    spaceId: string,
    workflowId: string,
    body: { name?: string; definition?: Envelope; paused?: boolean } & Partial<WorkflowSchedule>,
  ) {
    return this.request<ServerWorkflow>("PATCH", `/api/spaces/${spaceId}/workflows/${workflowId}`, body);
  }
  deleteWorkflow(spaceId: string, workflowId: string) {
    return this.request<void>("DELETE", `/api/spaces/${spaceId}/workflows/${workflowId}`);
  }
  /** What this person's workflows need from their devices. */
  workflowWork() {
    return this.request<WorkflowWork>("GET", "/api/workflows/work");
  }
  putWorkflowRuns(
    workflowId: string,
    body: {
      as_of: string;
      runs: { client_id: string; send_at: string; envelope: Envelope }[];
      replace?: boolean;
      definition?: Envelope;
    },
  ) {
    return this.request<void>("PUT", `/api/workflows/${workflowId}/runs`, body);
  }
  postWelcome(workflowId: string, body: { client_id: string; envelope: Envelope; user_ids: string[] }) {
    return this.request<void>("POST", `/api/workflows/${workflowId}/welcome`, body);
  }

  // Guests

  guestInvites(spaceId: string) {
    return this.request<GuestInvite[]>("GET", `/api/spaces/${spaceId}/guest-invites`);
  }
  createGuestInvite(spaceId: string, channelIds: string[]) {
    return this.request<GuestInvite>("POST", `/api/spaces/${spaceId}/guest-invites`, {
      channel_ids: channelIds,
    });
  }
  revokeGuestInvite(spaceId: string, code: string) {
    return this.request<void>("DELETE", `/api/spaces/${spaceId}/guest-invites/${encodeURIComponent(code)}`);
  }
  /** Adds people you can reach as guests in the channels. Someone already in the space keeps their role. */
  addGuests(spaceId: string, userIds: string[], channelIds: string[]) {
    return this.request<{ added: string[]; skipped: string[] }>("POST", `/api/spaces/${spaceId}/guests`, {
      user_ids: userIds,
      channel_ids: channelIds,
    });
  }

  // Channels shared between spaces

  shareChannel(spaceId: string, chatId: string) {
    return this.request<{ code: string }>("POST", `/api/spaces/${spaceId}/channels/${chatId}/share`);
  }
  revokeShare(spaceId: string, chatId: string) {
    return this.request<void>("DELETE", `/api/spaces/${spaceId}/channels/${chatId}/share`);
  }
  previewShare(code: string) {
    return this.request<SharePreview>("GET", `/api/spaces/shares/${encodeURIComponent(code)}`);
  }
  acceptShare(code: string, spaceId: string) {
    return this.request<Chat>("POST", `/api/spaces/shares/${encodeURIComponent(code)}`, {
      space_id: spaceId,
    });
  }
  /** The owning space takes another space out, or a space takes itself out. */
  stopSharing(spaceId: string, chatId: string, otherSpaceId: string) {
    return this.request<void>("DELETE", `/api/spaces/${spaceId}/channels/${chatId}/shares/${otherSpaceId}`);
  }

  // Pins, scheduled messages and reminders

  pins(chatId: string) {
    return this.request<ServerPin[]>("GET", `/api/chats/${chatId}/pins`);
  }
  pin(chatId: string, messageId: string) {
    return this.request<ServerPin[]>("PUT", `/api/chats/${chatId}/pins/${messageId}`);
  }
  unpin(chatId: string, messageId: string) {
    return this.request<ServerPin[]>("DELETE", `/api/chats/${chatId}/pins/${messageId}`);
  }
  schedule(
    chatId: string,
    body: {
      client_id: string;
      envelope: Envelope;
      send_at: string;
      thread_root?: string;
      mentions?: string[];
      attachments?: string[];
    },
  ) {
    return this.request<ServerScheduled>("POST", `/api/chats/${chatId}/scheduled`, body);
  }
  scheduled(chatId?: string) {
    return this.request<ServerScheduled[]>("GET", "/api/scheduled", undefined, { chat_id: chatId });
  }
  reschedule(id: string, sendAt: string) {
    return this.request<ServerScheduled>("PATCH", `/api/scheduled/${id}`, { send_at: sendAt });
  }
  cancelScheduled(id: string) {
    return this.request<void>("DELETE", `/api/scheduled/${id}`);
  }
  sendScheduledNow(id: string) {
    return this.request<void>("POST", `/api/scheduled/${id}/send`);
  }
  remind(chatId: string, messageId: string, remindAt: string) {
    return this.request<ServerReminder>("POST", `/api/chats/${chatId}/reminders`, {
      message_id: messageId,
      remind_at: remindAt,
    });
  }
  reminders(done = false) {
    return this.request<ServerReminder[]>("GET", "/api/reminders", undefined, done ? { done: "true" } : {});
  }
  updateReminder(id: string, body: { done?: boolean; remind_at?: string }) {
    return this.request<void>("PATCH", `/api/reminders/${id}`, body);
  }
  deleteReminder(id: string) {
    return this.request<void>("DELETE", `/api/reminders/${id}`);
  }

  // Status and quiet hours

  setStatus(body: { emoji: string; text: string; until: string | null }) {
    return this.request<User>("PUT", "/api/me/status", body);
  }
  clearStatus() {
    return this.request<User>("DELETE", "/api/me/status");
  }
  setQuietHours(body: QuietHours) {
    return this.request<User>("PUT", "/api/me/quiet-hours", body);
  }
  clearQuietHours() {
    return this.request<User>("DELETE", "/api/me/quiet-hours");
  }

  /** Your own chat, for notes, links and files you send yourself. Asking again returns the same one. */
  selfChat() {
    return this.request<Chat>("POST", "/api/chats/self");
  }
  updateChat(
    id: string,
    body: Partial<
      Pick<
        Chat,
        "title" | "about" | "only_admins_post" | "disappear_after" | "assistant_allowed" | "members_can_add"
      >
    >,
  ) {
    return this.request<Chat>("PATCH", `/api/chats/${id}`, body);
  }
  updateChatSettings(
    id: string,
    body: {
      muted_until?: string | null;
      mentions_only?: boolean;
      archived?: boolean;
      pinned?: boolean;
      marked_unread?: boolean;
      starred?: boolean;
      /** Channels only: go back to following your setting for the space. */
      follow_space?: boolean;
    },
  ) {
    return this.request<Chat>("PATCH", `/api/chats/${id}/me`, body);
  }
  /** Mark all as read, for every archived chat at once. Answers the chats that changed. */
  markArchivedRead() {
    return this.request<{ chat_ids: string[] }>("POST", "/api/archive/read");
  }
  chatPreferences() {
    return this.request<ChatPreferences>("GET", "/api/me/chat-settings");
  }
  setChatPreferences(body: Partial<ChatPreferences>) {
    return this.request<ChatPreferences>("PATCH", "/api/me/chat-settings", body);
  }
  /** Clear messages, or with `deleting`, Delete chat. Only for you, on all your devices. */
  clearChat(id: string, deleting = false) {
    return this.request<Chat>("POST", `/api/chats/${id}/clear`, { delete: deleting });
  }
  addMembers(id: string, userIds: string[]) {
    return this.request<Chat>("POST", `/api/chats/${id}/members`, { user_ids: userIds });
  }
  removeMember(id: string, userId: string) {
    return this.request<void>("DELETE", `/api/chats/${id}/members/${userId}`);
  }
  setRole(id: string, userId: string, role: "admin" | "member") {
    return this.request<void>("PATCH", `/api/chats/${id}/members/${userId}`, { role });
  }
  createInvite(id: string) {
    return this.request<{ code: string }>("POST", `/api/chats/${id}/invite`);
  }
  revokeInvite(id: string) {
    return this.request<void>("DELETE", `/api/chats/${id}/invite`);
  }
  previewInvite(code: string) {
    return this.request<{ chat_id: string; title: string; about: string; member_count: number }>(
      "GET",
      `/api/invites/${code}`,
    );
  }
  joinInvite(code: string) {
    return this.request<Chat>("POST", `/api/invites/${code}/join`);
  }
  messages(
    chatId: string,
    query: { after?: number; before?: number; limit?: number; kind?: "import"; thread?: string },
  ) {
    return this.request<ServerMessage[]>("GET", `/api/chats/${chatId}/messages`, undefined, query);
  }
  sendMessage(
    chatId: string,
    body: {
      client_id: string;
      kind: "message" | "reaction" | "edit" | "vote" | "notice" | "import";
      envelope: Envelope;
      relates_to?: string;
      /** The message a reply in a thread replies to. */
      thread_root?: string;
      mentions?: string[];
      /** Storage keys of encrypted files this message carries. */
      attachments?: string[];
      /** Files sent again from a message you can see, such as in a forward. */
      forwarded?: ForwardedFile[];
    },
  ) {
    return this.request<ServerMessage>("POST", `/api/chats/${chatId}/messages`, body);
  }
  /** Delete for me: the messages leave all your devices and nobody else's. */
  hideMessages(chatId: string, messageIds: string[]) {
    return this.request<void>("POST", `/api/chats/${chatId}/messages/hide`, { message_ids: messageIds });
  }
  starMessage(chatId: string, messageId: string) {
    return this.request<void>("PUT", `/api/chats/${chatId}/stars/${messageId}`);
  }
  unstarMessage(chatId: string, messageId: string) {
    return this.request<void>("DELETE", `/api/chats/${chatId}/stars/${messageId}`);
  }
  /** The ids of the messages you starred in a chat, newest star first. */
  chatStars(chatId: string) {
    return this.request<string[]>("GET", `/api/chats/${chatId}/stars`);
  }
  /** Your starred messages across chats, newest star first, a page at a time. */
  starredMessages(query: { before?: string; limit?: number } = {}) {
    return this.request<StarPage>("GET", "/api/stars", undefined, query);
  }
  /** The page around a message, for opening a link to it. 404 when you cannot open it. */
  messageContext(chatId: string, messageId: string, limit?: number) {
    return this.request<MessageContext>(
      "GET",
      `/api/chats/${chatId}/messages/${messageId}/context`,
      undefined,
      { limit },
    );
  }
  /** When each person got and read a message you sent. */
  messageInfo(chatId: string, messageId: string) {
    return this.request<MessageInfo>("GET", `/api/chats/${chatId}/messages/${messageId}/info`);
  }
  /** Mark unread from here: moves your read point back to just before this message. */
  markUnreadFrom(chatId: string, messageId: string) {
    return this.request<Chat>("POST", `/api/chats/${chatId}/unread-from`, { message_id: messageId });
  }
  deleteMessage(chatId: string, messageId: string) {
    return this.request<void>("DELETE", `/api/chats/${chatId}/messages/${messageId}`);
  }
  markRead(chatId: string, seq: number) {
    return this.request<void>("POST", `/api/chats/${chatId}/read`, { seq });
  }
  markDelivered(chatId: string, seq: number) {
    return this.request<void>("POST", `/api/chats/${chatId}/delivered`, { seq });
  }
  /** Tells the server which messages reached this device, for up to 500 chats at once. */
  ackDelivered(acks: { chat_id: string; seq: number }[]) {
    return this.request<void>("POST", "/api/delivered", { acks });
  }

  // Calls. The server signals and hands out LiveKit tokens. It never sees the media key.

  startCall(chatId: string, media: CallMedia) {
    return this.request<CallJoin>("POST", `/api/chats/${chatId}/calls`, { media });
  }
  joinCall(callId: string) {
    return this.request<CallJoin>("POST", `/api/calls/${callId}/join`);
  }
  /** Pass keepalive when the page is closing, so the server still hears about it. */
  leaveCall(callId: string, options: { keepalive?: boolean } = {}) {
    return this.request<CallInfo>("POST", `/api/calls/${callId}/leave`, undefined, undefined, options);
  }
  declineCall(callId: string) {
    return this.request<CallInfo>("POST", `/api/calls/${callId}/decline`);
  }
  callHistory() {
    return this.request<CallInfo[]>("GET", "/api/calls");
  }
  /** The call going on in a chat, such as a channel's huddle, or null. */
  activeCall(chatId: string) {
    return this.request<CallInfo | null>("GET", `/api/chats/${chatId}/calls/active`);
  }

  // Media. The server only ever sees ciphertext.

  createUpload(chatId: string, size: number, purpose?: "import") {
    return this.request<{ key: string; url: string; method: string }>("POST", "/api/media/uploads", {
      chat_id: chatId,
      size,
      ...(purpose ? { purpose } : {}),
    });
  }
  /** With `chunked`, asks as an app that reads chunked (version 2) files, which older apps cannot. */
  mediaUrl(key: string, opts: { chunked?: boolean } = {}) {
    return this.request<{ url: string }>("GET", "/api/media/url", undefined, {
      key,
      ...(opts.chunked ? { v: 2 } : {}),
    });
  }
  /** Starts a multipart upload of `size` stored bytes in parts of `partSize` bytes. */
  startMultipart(chatId: string, size: number, partSize: number, purpose?: "import") {
    return this.request<{ key: string; part_size: number; parts: number }>("POST", "/api/media/multipart", {
      chat_id: chatId,
      size,
      part_size: partSize,
      ...(purpose ? { purpose } : {}),
    });
  }
  /** Presigned PUTs for some parts, keyed by part number. At most 100 at a time. */
  multipartUrls(key: string, parts: number[]) {
    return this.request<{ urls: Record<string, string> }>("POST", "/api/media/multipart/urls", {
      key,
      parts,
    });
  }
  /** The parts storage already has, so an upload can carry on after them. */
  multipartParts(key: string) {
    return this.request<{ part_size: number; parts: { part: number; size: number }[] }>(
      "GET",
      "/api/media/multipart/parts",
      undefined,
      { key },
    );
  }
  completeMultipart(key: string) {
    return this.request<{ key: string }>("POST", "/api/media/multipart/complete", { key });
  }
  abortMultipart(key: string) {
    return this.request<{ ok: boolean }>("DELETE", "/api/media/multipart", undefined, { key });
  }

  // Plans. Checkout and the portal are refused in the iOS and Android apps.

  billing() {
    return this.request<BillingInfo>("GET", "/api/billing");
  }
  checkout(interval: BillingInterval) {
    return this.request<{ url: string }>("POST", "/api/billing/checkout", { interval });
  }
  billingPortal() {
    return this.request<{ url: string }>("POST", "/api/billing/portal");
  }

  // Push

  pushConfig() {
    return this.request<{ vapid_public_key: string | null }>("GET", "/api/push/config");
  }
  subscribePush(body: { kind: "webpush"; token: string; keys: { p256dh: string; auth: string } }) {
    return this.request<void>("POST", "/api/push/subscriptions", body);
  }
  unsubscribePush() {
    return this.request<void>("DELETE", "/api/push/subscriptions");
  }
}
